import type {ReactElement} from 'react';
import {useLayoutEffect, useRef, useState} from 'react';
import {Icon} from '@Components/icon-v2';
import {Text, TextSize} from '@Components/text';
import {IconSize} from '@Components/icon-v2/icon.types';
import {CircularProgressLoader, LOADER_COLOR, LOADER_SIZE} from '@Components/circular-progress-loader';
import {Dropdown} from '@Components/dropdown-v2';
import {ALIGNMENT_TYPE, DROPDOWN_POSITION} from '@Components/dropdown-v2/dropdown.types';
import {Tooltip, TOOLTIP_POSITION} from '@Components/tooltip';
import styles from './media-controls-item.module.scss';
import {useScrollDistance} from '@Hooks/useScrollDistance';

export interface MediaControlsItemProps {
  id: string;
  icon?: string;
  text?: string;
  tag?: ReactElement<any> | null;
  hoverTag?: ReactElement | null;
  isHoverTagAlwaysVisible?: boolean;
  tagContainerClassName?: string;
  className?: string;
  iconContainerClasses?: string;
  textColorClasses?: string;
  textPaddingClasses?: string;
  iconClasses?: string;
  isTextBold?: boolean;
  isLoading?: boolean;
  loadingText?: string;
  loaderType?: LOADER_COLOR;
  isSelected?: boolean;
  showAsListItem?: boolean;
  isDisabled?: boolean;
  disabledTooltipText?: string;
  compactIconPadding?: boolean;
  dropdownAlignment?: ALIGNMENT_TYPE;
  customIconObject?: ReactElement<any>;
  customPopup?: ReactElement<any>;
  dropdownContent?: ReactElement;

  onClick?(id: string): void;

  onDropdownOpen?(): void;
}

export function MediaControlsItem({
  tag = null,
  hoverTag = null,
  isHoverTagAlwaysVisible = false,
  text = '',
  icon = '',
  className = '',
  tagContainerClassName = '',
  iconContainerClasses = '',
  textColorClasses = '',
  textPaddingClasses = '',
  iconClasses = '',
  loadingText = '',
  loaderType = LOADER_COLOR.DARK,
  isTextBold = false,
  isLoading = false,
  isSelected = false,
  showAsListItem = false,
  onClick = (): void => {},
  onDropdownOpen = (): void => {},
  isDisabled = false,
  disabledTooltipText = '',
  compactIconPadding = false,
  dropdownAlignment = ALIGNMENT_TYPE.BOTTOM_END,
  customIconObject = undefined,
  customPopup = undefined,
  dropdownContent = undefined,
  ...props
}: MediaControlsItemProps): ReactElement<any> | null {
  const elementRef = useRef<HTMLLIElement>(null);
  const hoverTagSlotRef = useRef<HTMLDivElement>(null);
  const distance = useScrollDistance(elementRef);
  const [isHoveringDisabled, setIsHoveringDisabled] = useState<boolean>(false);
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const [isHoverTagRenderingEmpty, setIsHoverTagRenderingEmpty] = useState<boolean>(false);
  const isHoverTagActive = (isHovered || isHoverTagAlwaysVisible) && hoverTag !== null;

  useLayoutEffect(() => {
    const slot = hoverTagSlotRef.current;
    if (!slot) {
      return undefined;
    }
    const updateHoverTagEmptiness = (): void => {
      setIsHoverTagRenderingEmpty(slot.childNodes.length === 0);
    };
    updateHoverTagEmptiness();
    const observer = new MutationObserver(updateHoverTagEmptiness);
    observer.observe(slot, {childList: true});
    return (): void => {
      observer.disconnect();
    };
  }, [isHoverTagActive, hoverTag]);

  const shouldShowDisabledTooltip = isDisabled && disabledTooltipText !== '';

  const onItemMouseEnter = (): void => {
    if (shouldShowDisabledTooltip) {
      setIsHoveringDisabled(true);
    }
    setIsHovered(true);
  };

  const onItemMouseLeave = (): void => {
    if (shouldShowDisabledTooltip) {
      setIsHoveringDisabled(false);
    }
    setIsHovered(false);
  };

  const getDisabledTooltip = (): ReactElement | null => {
    if (!isHoveringDisabled || !shouldShowDisabledTooltip) {
      return null;
    }
    if (elementRef.current === null) {
      return null;
    }
    const rect = elementRef.current.getBoundingClientRect();
    return (
      <Tooltip
        id={`${props.id}-disabled-tooltip`}
        className={styles.disabledTooltip}
        position={TOOLTIP_POSITION.BOTTOM_MIDDLE}
        style={{
          position: 'fixed',
          bottom: `${window.innerHeight - rect.top + 16}px`,
          left: `${rect.left + rect.width / 2}px`,
        }}
        text={<Text val={disabledTooltipText} size={TextSize.XXSMALL} />}
      />
    );
  };

  const getTextColorClasses = (): string => {
    if (isDisabled) {
      return 'color-extra-light';
    }

    if (textColorClasses && textColorClasses !== '') {
      return textColorClasses;
    }

    return isSelected ? 'content-primary' : 'content-sub-text';
  };

  const getTextPaddingClasses = (): string => {
    if (textPaddingClasses && textPaddingClasses !== '') {
      return textPaddingClasses;
    }

    return 'spacing-p-r-6 spacing-p-l-6 ';
  };

  const getIconClasses = (): string => {
    if (iconClasses && iconClasses !== '') {
      return iconClasses;
    }
    return '';
  };

  const getContent = (): ReactElement<any> => {
    const textVal = isLoading ? (loadingText ?? window.i18next.t('pmwjs_loading')) : text;
    return (
      <>
        {!isLoading ? (
          <>{customIconObject ?? <Icon icon={icon} className={getIconClasses()} size={IconSize.SIZE_ICON_24} />}</>
        ) : (
          <CircularProgressLoader classes="_unmargin" size={showAsListItem ? LOADER_SIZE.SMALL : LOADER_SIZE.DEFAULT} color={loaderType} />
        )}
        <Text
          title={textVal}
          val={textVal}
          className={`${showAsListItem ? styles.textMarginLeft : 'spacing-m-t-1 '} ${getTextPaddingClasses()} ${styles.text} ${getTextColorClasses()}`}
          size={TextSize.XXSMALL}
          bold={isTextBold}
        />
      </>
    );
  };

  const renderHoverTagSlot = (): ReactElement | null => {
    if (!isHoverTagActive) {
      return null;
    }
    return <div ref={hoverTagSlotRef}>{hoverTag}</div>;
  };

  const getTag = (): ReactElement<any> | null => {
    const isHoverTagShowing = isHoverTagActive && !isHoverTagRenderingEmpty;
    if (!tag && !hoverTag) {
      return null;
    }
    return (
      <div className={`${showAsListItem ? '' : `${isHoverTagShowing ? styles.hoveredTagContainer : styles.gridTagContainer} spacing-m-r-1 spacing-m-l-1 spacing-m-t-1`} ${tagContainerClassName}`}>
        {renderHoverTagSlot()}
        {isHoverTagShowing ? null : tag}
      </div>
    );
  };

  const getCustomPopup = (): ReactElement<any> | null => {
    if (customPopup === undefined) {
      return null;
    }

    const distancePercentage = distance * 100;
    let repositionTriangle = false;
    let delta;
    if (distancePercentage <= 25) {
      delta = 7;
    } else if (distancePercentage <= 50) {
      delta = 4;
    } else if (distancePercentage <= 75) {
      delta = -2;
    } else if (distancePercentage <= 85) {
      repositionTriangle = true;
      delta = -14;
    } else if (distancePercentage <= 90) {
      repositionTriangle = true;
      delta = -20;
    } else {
      repositionTriangle = true;
      delta = -25;
    }

    const top = Math.floor(distancePercentage + delta);
    return distance > 0 && distance < 1 ? (
      <div className={styles.customPopupContainer} style={{top: `${top}%`}}>
        {customPopup}
        <div className={`${styles.triangle} ${repositionTriangle ? styles.middle : ''}`}></div>
      </div>
    ) : null;
  };

  const showAsGridItem = (): ReactElement => {
    const textVal = isLoading ? (loadingText ?? window.i18next.t('pmwjs_loading')) : text;

    const iconContainer = (
      <div
        className={`${styles.iconContainer} ${iconContainerClasses} ${isDisabled ? styles.isDisabled : ''} ${isSelected ? styles.selected : ''} hover-transition-all spacing-p-t-4 spacing-p-b-4 ${compactIconPadding ? 'spacing-p-r-4 spacing-p-l-4' : 'spacing-p-r-8 spacing-p-l-8'} flex-center border-s-standard radius-12`}
      >
        {!isLoading ? (
          <>{customIconObject ?? <Icon icon={icon} className={`${getIconClasses()} ${isDisabled ? styles.disabledIcon : ''}`} size={IconSize.SIZE_ICON_20} />}</>
        ) : (
          <CircularProgressLoader classes="_unmargin" size={LOADER_SIZE.SMALL} color={loaderType} />
        )}
      </div>
    );

    return (
      <li
        onClick={(): void => {
          onClick(props.id);
        }}
        onMouseEnter={onItemMouseEnter}
        onMouseLeave={onItemMouseLeave}
        id={props.id}
        ref={elementRef}
        className={`_full-width _position-relative _full-height flex-v-row gap-1 spacing-p-0`}
      >
        {dropdownContent !== undefined ? (
          <Dropdown
            onOpen={onDropdownOpen}
            popupOffset={8}
            doesCloseOnOuterClick
            doCloseOnInnerClick
            selector={iconContainer}
            popup={dropdownContent}
            alignment={dropdownAlignment}
            position={DROPDOWN_POSITION.FIXED}
            popUpHasCustomWidth
            popupClassName={styles.popup}
          />
        ) : (
          iconContainer
        )}
        <Text title={textVal} val={textVal} className={`${textPaddingClasses} ${styles.text} text-center ${getTextColorClasses()}`} size={TextSize.SM} bold={isTextBold} />
        {getTag()}
        {getCustomPopup()}
        {getDisabledTooltip()}
      </li>
    );
  };

  const showAsList = (): ReactElement<any> => {
    return (
      <>
        <li
          onClick={(): void => {
            if (!isLoading) {
              onClick(props.id);
            }
          }}
          onMouseEnter={onItemMouseEnter}
          onMouseLeave={onItemMouseLeave}
          id={props.id}
          className={`${styles.baseContainer} ${isSelected ? styles.selected : ''} ${isDisabled ? styles.isDisabled : ''} ${
            styles.listContainer
          } spacing-p-2 flex-row-justify-between flex-items-center hover-transition-all ${className}`}
          ref={elementRef}
        >
          <div className="flex-row-align-center">{getContent()}</div>
          {getTag()}
          {getDisabledTooltip()}
        </li>
        {getCustomPopup()}
      </>
    );
  };

  const showMediaItem = (): ReactElement<any> => {
    return showAsListItem ? showAsList() : showAsGridItem();
  };

  return showMediaItem();
}
